"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export interface PageChromeBreadcrumb {
  label: string;
  href?: string;
}

export interface PageChromeAction {
  label: string;
  href: string;
  icon?: "plus";
}

export interface PageChromeConfig {
  title: string;
  breadcrumbs?: PageChromeBreadcrumb[];
  parentNavigation?: PageChromeBreadcrumb;
  primaryAction?: PageChromeAction;
}

interface PageChromeEntry {
  owner: symbol;
  config: PageChromeConfig;
}

interface PageChromeContextValue {
  chrome: PageChromeConfig | null;
  register: (owner: symbol, config: PageChromeConfig) => void;
  unregister: (owner: symbol) => void;
}

const PageChromeContext = createContext<PageChromeContextValue>({
  chrome: null,
  register: () => undefined,
  unregister: () => undefined,
});

export function PageChromeProvider({ children }: { children: ReactNode }) {
  const [entry, setEntry] = useState<PageChromeEntry | null>(null);

  const register = useCallback((owner: symbol, config: PageChromeConfig) => {
    setEntry({ owner, config });
  }, []);

  const unregister = useCallback((owner: symbol) => {
    setEntry((current) => (current?.owner === owner ? null : current));
  }, []);

  const value = useMemo<PageChromeContextValue>(
    () => ({ chrome: entry?.config ?? null, register, unregister }),
    [entry, register, unregister]
  );

  return (
    <PageChromeContext.Provider value={value}>
      {children}
    </PageChromeContext.Provider>
  );
}

export function usePageChrome() {
  return useContext(PageChromeContext).chrome;
}

/**
 * 現在画面のタイトル・パンくず・主要操作をグローバルヘッダーへ登録する。
 * owner を照合して解除するため、画面遷移が重なっても新しい登録を消さない。
 */
export function usePageChromeRegistration(config: PageChromeConfig) {
  const { register, unregister } = useContext(PageChromeContext);
  const ownerRef = useRef(Symbol("page-chrome"));
  const signature = JSON.stringify(config);

  useEffect(() => {
    register(ownerRef.current, config);
    return () => unregister(ownerRef.current);
    // config は文字列化した内容が変わった時だけ再登録する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister, signature]);
}
